import {Injectable} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {createNumberFormat, formatDateWithFormat} from '../../common/formatters';
import {DisplayExchangeAccountFactory} from '../../factories/display-exchange-account.factory';
import {HtmlSymbolService} from '../../services/html-symbol.service';
import {PositionsStreamingService} from '../../widgets/components/positions-streaming';
import {AccountService} from '../accounts';
import {getOrderQtyCurrency, getOrderQtyMultiCurrencyInfoForPosition} from '../order-qty-multi-currency';
import {
    addCurrencyToDisplayDecimal,
    DisplayDecimal,
    DisplayDecimalWithCurrency,
    formatQuantityWithCurrency,
    formatReport,
    getEmptyDisplayDecimalAsDash,
    SymbolDecimalsService,
    SymbolFormats,
    USE_QTY_STORE_FORMAT
} from '../symbol-decimals';
import {getPositionIdentificationKey, Position} from './position';

export interface DisplayPosition {
    position: Position;

    positionKey: string;
    accountName: string;
    quantity: DisplayDecimalWithCurrency;
    quantitySignClass: string;
    qtyCurrency: string;
    baseAssetQty?: DisplayDecimalWithCurrency;
    baseCurrency: string;
    combinedQtyTooltip: string;
    side: string;
    price: DisplayDecimal;
    mtm: DisplayDecimal;
    realizedPnL: DisplayDecimal;
    realizedPnLSignClass: string;
    liquidationPrice: DisplayDecimal;
    openPnL: DisplayDecimal;
    openPnLSignClass: string;
    totalPnL: DisplayDecimal;
    totalPnLSignClass: string;
    assetClass: string | SafeHtml;
    openDateTime: string | SafeHtml;
    isCloseable: boolean;
}

interface SignNamingMap {
    POSITIVE: string;
    NEGATIVE: string;
}

const SIGN_CLASS: SignNamingMap  = {
    POSITIVE: '-positive',
    NEGATIVE: '-negative'
};
const SIDE_BY_SIGN: SignNamingMap = {
    POSITIVE: 'components.widgets.positions-widget.side_long',
    NEGATIVE: 'components.widgets.positions-widget.side_short'
};

type ValueFormatter<T extends DisplayDecimal = DisplayDecimal> = (value: number) => T;

const ZERO = 0;
const SIDE_PLACEHOLDER = '—';

export const POSITION_DATE_FORMAT = 'M/DD/YYYY HH:mm';

@Injectable()
export class DisplayPositionFactory {

    public static getTotalPnL(position: Position): number {

        /**
         * In HG project these fields are not calculated, but retrieved from remote API.
         * "Ext" means "external".
         */
        // const totalPnL: number = position.baseMarketCost
        //     ? new Decimal(position.totalPnl)
        //         .add(position.marketCost).sub(position.baseMarketCost)
        //         .toNumber()
        //     : position.totalPnl;

        return position.totalPnlExt;
    }

    private static getSign(value: number, namingMap: SignNamingMap): string {
        return value > 0 ? namingMap.POSITIVE : value < 0 ? namingMap.NEGATIVE : '';
    }

    private mdash: SafeHtml = this.htmlSymbolService.mdash();

    private readonly baseAssetQtyFormat = createNumberFormat({
        isDecimalRequired: false,
        decimalCount:      11 // Maximum decimal count for the app.
    });

    constructor(
        private htmlSymbolService: HtmlSymbolService,
        private accountService: AccountService,
        private symbolDecimalsService: SymbolDecimalsService
    ) {}

    // tslint:disable:cyclomatic-complexity
    public createDisplayPosition(position: Position): DisplayPosition {
        const {security} = position;

        /**
         * In HG project these fields are not calculated, but retrieved from remote API.
         * "Ext" means "external".
         */
        // const absMarketCost = position.marketCost && Math.abs(position.marketCost);

        const openPnL: number = position.openPnlExt;
        const totalPnL: number = DisplayPositionFactory.getTotalPnL(position);
        const absMarketCost = position.marketCostExt && Math.abs(position.marketCostExt);

        const symbolFormats: SymbolFormats = this.symbolDecimalsService.getSecurityFormats(security);

        const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForPosition(position);

        const qtyCurrency = position.quantity
            ? getOrderQtyCurrency(security, qtyMultiCurrencyInfo)
            : '';

        const reportFormatter: ValueFormatter = value => formatReport(value, symbolFormats);
        const quantityFormatter: ValueFormatter<DisplayDecimalWithCurrency> = value =>
            formatQuantityWithCurrency(value, qtyCurrency, symbolFormats, USE_QTY_STORE_FORMAT);

        const displayQty = quantityFormatter(position.quantity);

        addCurrencyToDisplayDecimal(displayQty, qtyCurrency);

        const baseCurrency = security.baseAsset.symbol;
        const baseAssetQtyValue = (qtyCurrency === baseCurrency)
            ? undefined
            : position.baseValue || undefined;
        const baseAssetQty = this.createDisplayBaseAssetQty(baseAssetQtyValue, baseCurrency);

        const combinedQtyTooltip = [
            baseAssetQtyValue && `${baseAssetQty.value} ${baseCurrency}`,
            displayQty && displayQty.value && `${displayQty.value} ${qtyCurrency}`
        ].filter(Boolean).join(' / ');

        return {
            position,

            positionKey: getPositionIdentificationKey(position),

            accountName: DisplayExchangeAccountFactory.getDisplayAccountName(
                this.accountService.getAccountByIdFromCache(position.accountId)
            ),

            quantity:          displayQty,
            quantitySignClass: DisplayPositionFactory.getSign(position.quantity, SIGN_CLASS),
            qtyCurrency,

            baseAssetQty,
            baseCurrency,

            combinedQtyTooltip,

            side:             DisplayPositionFactory.getSign(position.quantity, SIDE_BY_SIGN),
            price:            reportFormatter(position.averagePrice),
            mtm:              reportFormatter(absMarketCost),
            liquidationPrice: reportFormatter(position.liquidationPrice),

            realizedPnL:          reportFormatter(position.realizedPnl),
            realizedPnLSignClass: DisplayPositionFactory.getSign(position.realizedPnl, SIGN_CLASS),

            openPnL:          reportFormatter(openPnL),
            openPnLSignClass: DisplayPositionFactory.getSign(openPnL, SIGN_CLASS),

            totalPnL:          reportFormatter(totalPnL),
            totalPnLSignClass: DisplayPositionFactory.getSign(totalPnL, SIGN_CLASS),

            assetClass:   position.security.assetClass || this.mdash,
            openDateTime: position.openDateTime
                              ? formatDateWithFormat(position.openDateTime, POSITION_DATE_FORMAT)
                              : this.mdash,

            isCloseable: PositionsStreamingService.isPositionCloseable(position)
        };
    }

    public createDisplayEmptyPosition(): DisplayPosition {
        return {
            position:             null,
            positionKey:          '',
            accountName:          null,
            quantity:             {value: String(ZERO), class: '', currencySymbol: ''},
            quantitySignClass:    '',
            qtyCurrency:          '',
            baseAssetQty:         null,
            baseCurrency:         '',
            combinedQtyTooltip:   '',
            side:                 SIDE_PLACEHOLDER, // Not this.mdash because we use language pipe for this value
            price:                getEmptyDisplayDecimalAsDash(),
            mtm:                  getEmptyDisplayDecimalAsDash(),
            liquidationPrice:     getEmptyDisplayDecimalAsDash(),
            realizedPnL:          getEmptyDisplayDecimalAsDash(),
            realizedPnLSignClass: '',
            openPnL:              getEmptyDisplayDecimalAsDash(),
            openPnLSignClass:     '',
            totalPnL:             getEmptyDisplayDecimalAsDash(),
            totalPnLSignClass:    '',
            openDateTime:         this.mdash,
            assetClass:           this.mdash,
            isCloseable:          false
        };
    }

    /**
     * Formats the base asset qty w/o trailing zeroes.
     * @param {number} baseAssetQty
     * @param {string} baseCurrency
     */
    private createDisplayBaseAssetQty(baseAssetQty: number, baseCurrency: string): DisplayDecimalWithCurrency {
        const isInvalidValue = !baseAssetQty && baseAssetQty !== 0;
        const displayValue: string = isInvalidValue ? '' : this.baseAssetQtyFormat.format(baseAssetQty);

        return !isInvalidValue
            ? {
                value:          displayValue,
                class:          '',
                originalValue:  baseAssetQty,
                currencySymbol: baseCurrency
            }
            : undefined;
    }

}
